import { CheckboxCard, ListItemLayout, ListLayout, Pager, SearchBar } from '@keplerco/core';
import React, { useEffect, useState } from 'react';
import { useAppActions, useAppState } from '../../../../overmind';
import { CompanySkillListResponse } from '../../../../models/overmind/company-entity';
import { Skill } from '../../../../models/refactored/skill';
import classNames from 'classnames';
import { EmptyState } from '../../../../components/general/empty-state/empty-state';
import { SearchSkillsRequest } from '../../../../models/overmind/search-params';
import { SelectionEntity } from '../../../../components/panels/selection/selection.models';
import { SkeletonOverlay } from '../../../../components/general/loading-state/skeleton-overlay';
import selectionPanelStyles from '../../../../components/panels/selection/selection.module.css';

export function SkillSelectionList({ selectedEntities, setSelectedEntities }: { selectedEntities: SelectionEntity<Skill>[]; setSelectedEntities: (selectedEntities: SelectionEntity<Skill>[]) => void }): React.ReactElement {
  const actions = useAppActions();
  const { companyVariables } = useAppState();

  const [isLoading, setIsLoading] = useState<boolean>(false);
  const [isSearching, setIsSearching] = useState<boolean>(false);
  const [search, setSearch] = useState<string>('');
  const [page, setPage] = useState<number>(1);
  const [companySkills, setCompanySkills] = useState<CompanySkillListResponse>();

  async function getSkills(search: string, page: number) {
    setSearch(search);
    setPage(page);

    const request: SearchSkillsRequest = {
      search,
      sortAscending: true,
      page,
      pageSize: 5,
      companySlug: companyVariables.slug!,
      searchGlobally: false,
    };

    const response = await actions.getCompanySkills(request);
    setCompanySkills(response);
  }

  useEffect(() => {
    async function initialize() {
      setIsLoading(true);
      await getSkills(search, page);
      setIsLoading(false);
    }

    initialize();
  }, []);

  function handleSkillSelection(skill: Skill, isSelected: boolean) {
    if (isSelected) {
      setSelectedEntities([...selectedEntities, { slug: skill.slug, name: skill.name, entity: skill }]);
    } else {
      setSelectedEntities(selectedEntities.filter(selectedSkill => selectedSkill.slug !== skill.slug));
    }
  }

  return (
    <SkeletonOverlay isLoading={isLoading}>
      <div className={selectionPanelStyles.selectionList}>
        <SearchBar
          loading={isSearching}
          label="Search"
          onInput={async event => {
            setIsSearching(true);
            await getSkills((event.target as HTMLInputElement).value, 1);
            setIsSearching(false);
          }}
          responsive
        />

        {!!companySkills?.skills.length ? (
          <React.Fragment>
            <ListLayout gap={12}>
              {companySkills.skills.map(companySkill => {
                const isSelected = selectedEntities.some(selectedEntity => selectedEntity.slug === companySkill.slug);

                return (
                  <ListItemLayout key={companySkill.slug}>
                    <CheckboxCard id={companySkill.slug!} checked={isSelected} onChange={() => handleSkillSelection(companySkill as Skill, !isSelected)}>
                      <div className="card">
                        <h5>{companySkill.name}</h5>
                        <div style={{ color: 'var(--text_1)' }}>{companySkill.description}</div>
                      </div>
                    </CheckboxCard>
                  </ListItemLayout>
                );
              })}
            </ListLayout>

            <div className={classNames({ invisible: companySkills.totalPages <= 1 })}>
              <Pager
                activePageNumber={page}
                pageCount={companySkills.totalPages}
                onPageChange={async page => {
                  setIsLoading(true);
                  await getSkills(search, page);
                  setIsLoading(false);
                }}
              />
            </div>
          </React.Fragment>
        ) : (
          <EmptyState />
        )}
      </div>
    </SkeletonOverlay>
  );
}
